import axios, { type CancelTokenSource } from "axios";
import { List, Map } from "immutable";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GenerateProcess } from "redux-process";

import { ButtonStandard, Pagination } from "@performyard/py-components";

import DirectoryCsvDownloadButton from "./DirectoryCsvDownloadButton";
import DirectoryTable from "./DirectoryTable";

import { useRbacPrimaryRolePermissions } from "../../../api/hooks";

import { SearchUsers } from "../../../actions/users.actions";

import { getUserID } from "../../../selectors/app";

import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ChangeColumnsModal from "../ChangeColumnsModal";
import { type DirectoryColumn, buildDirectoryRows } from "./rows";

/**
 * The configurable directory columns, as resolved by the API and stashed in the company
 * reducer. Display Name is prepended by the table and never appears here.
 */
const useDirectoryColumns = (): DirectoryColumn[] => {
	const directoryFields = useSelector((state: any) => state.company.get("directoryFields", List())) as List<
		Map<string, any>
	>;

	return useMemo(
		() =>
			directoryFields.toArray().map((field) => {
				const type = field.get("type") as string;
				return {
					_id: String(field.get("_id")),
					name: field.get("name") as string,
					// Custom-field dates carry a meaningless time component; treat them as dumb dates
					// so they render the same calendar day in every timezone.
					type: type === "date" ? "dumb-date" : type,
				};
			}),
		[directoryFields]
	);
};

const DirectoryTab = (): React.ReactElement => {
	const dispatch = useDispatch();
	const { pageNumber, setPageNumber, searchValue, debouncedSearchValue, setDebouncedSearch } =
		useInfoPageTemplateQueryState({ defaultSortName: "name", defaultPageNumber: 1, searchDebounceMs: 300 });

	const [isChangeColumnsOpen, setIsChangeColumnsOpen] = useState(false);
	// Bumped after a column save to force the search to re-run. The search response is what
	// repopulates `company.directoryFields` (see middleware/APIResponse), so re-running it is
	// how the new column set reaches the table.
	const [columnsRevision, setColumnsRevision] = useState(0);
	const searchCancelTokenSource = useRef<CancelTokenSource | null>(null);

	const columns = useDirectoryColumns();
	const currentUserId = useSelector(getUserID) as string | undefined;
	const userMap = useSelector((state: any) => state.users.get("users", Map())) as Map<string, any>;
	const directoryList = useSelector((state: any) => state.users.get("directoryList", List())) as List<string>;
	const departmentMap = useSelector((state: any) => state.departments.get("departmentMap", Map())) as Map<
		string,
		any
	>;
	const rolesMap = useSelector((state: any) => state.roles.get("rolesMap", Map())) as Map<string, any>;
	const isFetching = useSelector((state: any) =>
		state.users.getIn(["_currentQuery", "isFetching"], false)
	) as boolean;
	const pageCount = useSelector((state: any) => state.users.getIn(["_currentQuery", "pageCount"], 1)) as number;

	const { data: permissions } = useRbacPrimaryRolePermissions("company_settings");
	const canChangeColumns = !!permissions?.includes("company_settings:editSettings");

	// The search API needs the column ids so it can return those custom fields on each user.
	const fieldIds = useMemo(() => columns.map((column) => column._id), [columns]);
	const fieldIdsKey = fieldIds.join(",");

	useEffect(() => {
		searchCancelTokenSource.current?.cancel("Request canceled by newer search");
		searchCancelTokenSource.current = axios.CancelToken.source();

		dispatch(
			SearchUsers(
				{
					query: debouncedSearchValue,
					fieldIds,
					page: pageNumber,
					// The rolodex owned the first/last-name sort toggle and is gone (PY-13545), so the
					// directory now always sorts on first name — the legacy cookie default.
					lastName: false,
				},
				undefined,
				undefined,
				undefined,
				undefined,
				searchCancelTokenSource.current.token
			) as any
		);
	}, [dispatch, debouncedSearchValue, pageNumber, fieldIdsKey, columnsRevision]);

	const rows = useMemo(
		() => buildDirectoryRows({ directoryList, userMap, departmentMap, rolesMap, columns }),
		[directoryList, userMap, departmentMap, rolesMap, columns]
	);

	const handleDownloadCsv = async (): Promise<string> => {
		const res: any = await dispatch(GenerateProcess("GenerateEmployeeCSV", { directoryFields: true }) as any);
		return res.response.get("jobID");
	};

	return (
		<div data-testid="directory-tab">
			<div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
				<div className="w-full min-w-0 sm:flex-1">
					<StandardToolbarActionRow
						searchInput={searchValue}
						onSearchInputChange={(value) => setDebouncedSearch(value)}
						searchPlaceholder="Search employees..."
					/>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<DirectoryCsvDownloadButton onStart={handleDownloadCsv} />
					{canChangeColumns ? (
						<div data-testid="change-columns-button">
							<ButtonStandard
								variant="outline"
								color="neutral"
								onClick={() => setIsChangeColumnsOpen(true)}
							>
								Change Columns
							</ButtonStandard>
						</div>
					) : null}
				</div>
			</div>

			<DirectoryTable rows={rows} columns={columns} loading={isFetching} currentUserId={currentUserId} />

			{pageCount > 1 ? (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			) : null}

			{isChangeColumnsOpen ? (
				<ChangeColumnsModal
					isOpen={isChangeColumnsOpen}
					selectedColumns={columns}
					onClose={() => setIsChangeColumnsOpen(false)}
					onSaved={() => setColumnsRevision((revision) => revision + 1)}
				/>
			) : null}
		</div>
	);
};

export default DirectoryTab;
